'use client';

import React from 'react';
import styled, { keyframes, css } from 'styled-components';

/**
 * Skeleton Props
 */
export interface SkeletonProps {
  /** Width of the skeleton */
  width?: string | number;
  /** Height of the skeleton */
  height?: string | number;
  /** Border radius */
  borderRadius?: string | number;
  /** Variant type */
  variant?: 'text' | 'circular' | 'rectangular';
  /** Number of lines for text variant */
  count?: number;
  /** Custom className */
  className?: string;
  /** Animation speed */
  animation?: 'pulse' | 'wave' | 'none';
  /** Custom style */
  style?: React.CSSProperties;
}

// ============================================================================
// Animations
// ============================================================================

const pulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  100% {
    opacity: 1;
  }
`;

const wave = keyframes`
  0% {
    transform: translateX(-100%);
  }
  50% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

// ============================================================================
// Styled Components
// ============================================================================

const SkeletonElement = styled.div<{
  $width?: string | number;
  $height?: string | number;
  $borderRadius?: string | number;
  $variant: 'text' | 'circular' | 'rectangular';
  $animation: 'pulse' | 'wave' | 'none';
}>`
  background-color: #e5e7eb;
  width: ${({ $width }) => 
    typeof $width === 'number' ? `${$width}px` : $width || '100%'};
  height: ${({ $height, $variant }) => {
    if ($height) {
      return typeof $height === 'number' ? `${$height}px` : $height;
    }
    return $variant === 'text' ? '1em' : '100%';
  }};
  border-radius: ${({ $borderRadius, $variant }) => {
    if ($borderRadius) {
      return typeof $borderRadius === 'number' ? `${$borderRadius}px` : $borderRadius;
    }
    if ($variant === 'circular') return '50%';
    if ($variant === 'text') return '4px';
    return '8px';
  }};
  
  ${({ $animation }) => {
    if ($animation === 'pulse') {
      return css`
        animation: ${pulse} 1.5s ease-in-out infinite;
      `;
    }
    if ($animation === 'wave') {
      return css`
        position: relative;
        overflow: hidden;
        
        &::after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          transform: translateX(-100%);
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.2) 20%,
            rgba(255, 255, 255, 0.5) 60%,
            rgba(255, 255, 255, 0)
          );
          animation: ${wave} 2s infinite;
        }
      `;
    }
    return '';
  }}
`;

const SkeletonContainer = styled.div<{ $count?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $count }) => ($count && $count > 1 ? '8px' : '0')};
`;

// ============================================================================
// Component
// ============================================================================

/**
 * Skeleton Component
 * 
 * A placeholder component for loading states that mimics the shape of content.
 * 
 * @component
 * @example
 * ```tsx
 * // Simple skeleton
 * <Skeleton width="200px" height="20px" />
 * 
 * // Text skeleton with multiple lines
 * <Skeleton variant="text" count={3} />
 * 
 * // Circular skeleton (e.g., for avatar)
 * <Skeleton variant="circular" width={40} height={40} />
 * 
 * // Rectangular skeleton with wave animation
 * <Skeleton variant="rectangular" width="100%" height="200px" animation="wave" />
 * ```
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  width,
  height,
  borderRadius,
  variant = 'rectangular',
  count = 1,
  className,
  animation = 'pulse',
  style,
}) => {
  if (count > 1) {
    return (
      <SkeletonContainer $count={count} className={className}>
        {Array.from({ length: count }).map((_, index) => (
          <SkeletonElement
            key={index}
            $width={width}
            $height={height}
            $borderRadius={borderRadius}
            $variant={variant}
            $animation={animation}
          />
        ))}
      </SkeletonContainer>
    );
  }

  return (
    <SkeletonElement
      $width={width}
      $height={height}
      $borderRadius={borderRadius}
      $variant={variant}
      $animation={animation}
      className={className}
      style={style}
    />
  );
};

export default Skeleton;

